import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";

import GroupShareForm from "./GroupShareForm";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/GroupShare.scss";

/**
 * GroupShare Component
 *
 * This component manages the UI for sharing access to a selected Elementary Progress Report (EPR)
 * with colleagues. It allows users to:
 * - Select an EPR profile if more than one is available.
 * - Choose a sharing mode (Whole Class or Homeroom Groups).
 * - Pick a specific homeroom group when in Group mode.
 * - Display and manage shares using the GroupShareForm.
 *
 * Props:
 * @param {Object} state - Global or parent state containing `gradingProfiles`, `selectedEpr`, etc.
 * @param {Function} dispatch - Dispatch function for state updates (e.g. setting selected EPR).
 */

const GroupShare = ({ dispatch, state }) => {
    const { gradingProfileId, homeroomGroupId } = useParams();
    const { gradingProfiles, selectedEpr } = state;
    const { state: globalState } = useContext(GlobalContext);
    const { currentTimePeriodDto, schoolYearDto } = globalState || {};

    const [shareView, setShareView] = useState(null);
    const [activeGroupId, setActiveGroupId] = useState(null);

    const groupGroups = selectedEpr?.homeroomGroups?.filter((g) => g.type === "GROUP") || [];
    const wholeClassGroup = selectedEpr?.homeroomGroups?.find((g) => g.type === "WHOLE_CLASS");
    const shouldShowEprSelect = !gradingProfileId && gradingProfiles?.length > 1;
    const groupModeAvailable = groupGroups.length > 0;

    const selectedHomeroomGroupId =
        shareView === "GROUP" ? activeGroupId : wholeClassGroup?.key || "WHOLE_CLASS_PLACEHOLDER";

    const handleEprChange = (profile) => {
        dispatch({ type: "SET_SELECTED_EPR", payload: profile });
        setShareView(null);
        setActiveGroupId(null);
    };

    /**
     * Sets the selected EPR (Elementary Progress Report) profile based on routing parameters or default logic.
     *
     * - If `gradingProfileId` is present in the URL and matches a profile in `gradingProfiles`, that profile is selected.
     * - If no EPR is selected but exactly one profile is available, that profile is selected by default.
     *
     * This ensures the component always has a selected profile to work with when rendering share options.
     */
    useEffect(() => {
        if (!selectedEpr && gradingProfiles.length > 0 && gradingProfileId) {
            const profileToSet = gradingProfiles.find((p) => p.key === gradingProfileId);
            if (profileToSet) {
                dispatch({ type: "SET_SELECTED_EPR", payload: profileToSet });
            }
        }
        if (!selectedEpr && gradingProfiles.length === 1) {
            dispatch({ type: "SET_SELECTED_EPR", payload: gradingProfiles[0] });
        }
    }, [gradingProfileId, gradingProfiles, selectedEpr, dispatch]);

    /**
     * Determines the share view mode ("GROUP" or "WHOLE_CLASS") based on the presence of a `homeroomGroupId` in the URL
     * and its corresponding group type in the selected EPR.
     *
     * - If a matching group of type "GROUP" is found, the view is set to "GROUP" and that group becomes active.
     * - Otherwise, defaults to "WHOLE_CLASS".
     *
     * This controls which interface is shown to the user after EPR selection.
     */
    useEffect(() => {
        if (!selectedEpr) return;

        if (homeroomGroupId) {
            const match = selectedEpr.homeroomGroups.find((g) => g.key.toString() === homeroomGroupId);
            if (match?.type === "GROUP") {
                setShareView("GROUP");
                setActiveGroupId(match.key);
            } else {
                setShareView("WHOLE_CLASS");
            }
        } else {
            setShareView("WHOLE_CLASS");
        }
    }, [homeroomGroupId, selectedEpr, groupModeAvailable]);

    return (
        <div className="right-side-wrapper">
            <div className="right-page-title">
                <h4>
                    EPR Share{" "}
                    {selectedEpr && (
                        <span>
                            &ndash;{" "}
                            {selectedEpr.gradeName === "K"
                                ? "Kindergarten"
                                : `${selectedEpr.gradeName} Grade Homeroom EPR`}
                        </span>
                    )}
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto.name}
                </h5>
            </div>
            <hr />
            <div className="shares-helper-text">
                Now that you have rostered your homeroom EPR, you may wish to share access for student rating and
                comment entry with your colleagues. Below you will be able to select share options for all students or
                (as appliable) share options for the homeroom groups you have created.
            </div>
            {shouldShowEprSelect && (
                <section className="epr-selector">
                    <h5>Select a Grade:</h5>
                    {gradingProfiles?.length > 1 && (
                        <>
                            <label className="visually-hidden" htmlFor="selectGradeShares">
                                Select a Grade
                            </label>
                            <select
                                className="epr-select-dropdown"
                                id="selectGradeShares"
                                value={selectedEpr?.key || ""}
                                onChange={(e) => {
                                    const selected = gradingProfiles.find(
                                        (p) => p.key.toString() === e.target.value.toString()
                                    );
                                    handleEprChange(selected);
                                }}
                            >
                                <option value="" disabled>
                                    Select Grade
                                </option>
                                {gradingProfiles
                                    .sort((a, b) => a.grade - b.grade)
                                    .map((profile) => (
                                        <option key={profile.key} value={profile.key}>
                                            {profile.gradeName === "K" ? "Kindergarten" : `${profile.gradeName} Grade`}
                                        </option>
                                    ))}
                            </select>
                        </>
                    )}
                </section>
            )}

            {selectedEpr && (
                <section className="share-mode">
                    <h5>Choose Sharing Mode:</h5>
                    <div className="share-mode-options">
                        <div className="mode-wrapper">
                            <button
                                className={`button-outline ${shareView === "WHOLE_CLASS" ? "active" : ""}`}
                                onClick={() => {
                                    setShareView("WHOLE_CLASS");
                                }}
                            >
                                <div className="button-content">
                                    <div className="button-label">Whole Class</div>
                                    <div className="button-helper">
                                        (Sharing all students to Art, Music, PE Teachers)
                                    </div>
                                </div>
                            </button>
                        </div>
                        <div className="mode-wrapper">
                            <button
                                className={`button-outline ${shareView === "GROUP" ? "active" : ""}`}
                                onClick={() => {
                                    setShareView("GROUP");
                                    setActiveGroupId(groupGroups[0]?.key);
                                }}
                                disabled={!groupModeAvailable}
                            >
                                <div className="button-content">
                                    <div className="button-label">Homeroom Groups</div>
                                    <div className="button-helper">
                                        (Sharing specific students for differentiated instruction)
                                    </div>
                                </div>
                            </button>
                            {!groupModeAvailable && (
                                <p className="mode-help-text">This EPR has no homeroom groups available.</p>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {shareView === "GROUP" && groupGroups.length > 0 && (
                <section className="group-tabs animated-slide">
                    <h5>Choose a Group</h5>

                    <div className="group-tab-list">
                        {groupGroups.map((group) => (
                            <button
                                key={group.key}
                                className={`button-outline ${activeGroupId === group.key ? "active" : ""}`}
                                onClick={() => {
                                    setActiveGroupId(group.key);
                                }}
                            >
                                {group.name}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {selectedEpr && selectedHomeroomGroupId && (
                <div className="animated-slide" key={selectedHomeroomGroupId}>
                    <GroupShareForm
                        dispatch={dispatch}
                        gradingProfileId={selectedEpr.key}
                        homeroomGroupId={selectedHomeroomGroupId}
                        selectedEpr={selectedEpr}
                        state={state}
                    />
                </div>
            )}
        </div>
    );
};

GroupShare.propTypes = {
    state: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired
};

export default GroupShare;
